import { useState } from "react";
import { GENRES, REGIONS, YEARS } from "@/lib/categories";

interface FilterPanelProps {
  genre: string;
  region: string;
  year: string;
  onChange: (patch: { genre?: string; region?: string; year?: string }) => void;
  onReset: () => void;
}

interface GroupProps {
  label: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
}

function Group({ label, options, value, onSelect }: GroupProps) {
  return (
    <div className="border-b border-background-200/50 py-3 last:border-b-0 last:pb-0">
      <p className="text-xs text-foreground-500">{label}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = option === value;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onSelect(option)}
              className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1 text-xs transition-colors ${
                active
                  ? "border-primary-500 bg-primary-500 font-medium text-background-50"
                  : "border-background-200 bg-background-50 text-foreground-300 hover:border-primary-500/50 hover:text-foreground-50"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterPanel({
  genre,
  region,
  year,
  onChange,
  onReset,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false);
  const typeOptions = ["全部", ...GENRES.map((item) => item.name)];
  const activeCount =
    (region !== "全部" ? 1 : 0) + (year !== "全部" ? 1 : 0) + (genre !== "全部" ? 1 : 0);

  return (
    <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100/70 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:self-start lg:overflow-y-auto">
      <div className="flex items-center justify-between gap-2 px-4 py-3 lg:px-5">
        <h2 className="flex items-center gap-2 font-heading text-sm font-medium text-foreground-100">
          <i className="ri-filter-3-line text-base text-primary-300" />
          筛选影片
          {activeCount > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-semibold text-background-50">
              {activeCount}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-xs text-foreground-400 transition-colors hover:text-accent-300"
          >
            <i className="ri-refresh-line text-sm" />
            重置
          </button>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-label="展开筛选条件"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-300 lg:hidden"
          >
            <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`} />
          </button>
        </div>
      </div>

      <div
        className={`${
          open ? "block" : "hidden"
        } border-t border-background-200/60 px-4 pb-4 pt-1 lg:block lg:px-5 lg:pb-4`}
      >
        <Group
          label="类型"
          options={typeOptions}
          value={genre}
          onSelect={(value) => onChange({ genre: value })}
        />
        <Group
          label="地区"
          options={REGIONS}
          value={region}
          onSelect={(value) => onChange({ region: value })}
        />
        <Group
          label="年份"
          options={YEARS}
          value={year}
          onSelect={(value) => onChange({ year: value })}
        />
      </div>
    </div>
  );
}